/**
 * Confirmation dialog for deleting a CPT / J-code from an order.
 *
 * Reads the item being deleted (`itemId`, `cptCode`, `cptDescription`)
 * and the in-flight `isDeleting` flag from Redux modal `metaData`,
 * which the caller is expected to populate when opening the modal.
 *
 * @module DeleteConfirmationModal
 */
import { useSelector } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import WarningIcon from "../../../svg/warningIcon";

/** Props for {@link DeleteConfirmationModal}. */
interface DeleteConfirmationModalProps {
  /**
   * Invoked when the user confirms deletion. Receives the `itemId`
   * pulled from Redux `metaData` (defaulted to "" if unset).
   */
  onDeleteClick?: (itemId: string) => void;
  /** Invoked when the user clicks "Cancel". */
  onCancelClick?: () => void;
  /** Invoked when the user dismisses the modal via the close icon. */
  onCloseClick?: () => void;
}

/**
 * Renders the delete-confirmation dialog for a CPT / J-code row.
 * The save button switches to a "Deleting..." disabled state while
 * `metaData.isDeleting` is true.
 */
const DeleteConfirmationModal = (props: DeleteConfirmationModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const { cptCode, cptDescription, itemId, isDeleting } = metaData ?? {};
  return (
    <Modal
      dialogId={ModalId.DELETE_CONFIRMATION_MODAL}
      onSave={() => props.onDeleteClick?.(itemId)}
      title={"Delete CPT Code"}
      saveButtonText={isDeleting ? "Deleting..." : "Yes"}
      cancelText={"Cancel"}
      disableSave={isDeleting}
      onCancel={props?.onCancelClick ?? (() => {})}
      onClose={props?.onCloseClick ?? (() => {})}
    >
      <div className="delete-cpt-modal-container flex flex-col items-center justify-center gap-4 p-4">
        <div className="icon">
          <WarningIcon />
        </div>
        <div className="text">
          Are you sure you want to delete this CPT Code?
        </div>
        <div className="cpt-code-details w-full truncate rounded bg-primaryGray-15 p-4 text-center text-small font-semibold">
          {cptCode} - {cptDescription}
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmationModal;
